import React from 'react';
import './HexGrid.css';
import { getNearestTownHint, getOddROffsetNeighbors } from '../utils/hexWorldMapUtils';

interface HexTile {
  id: number;
  x: number;
  y: number;
  tileType: string;
  biomeType: string;
  zoneLevel: number;
  name: string;
  description: string;
  requiredLevel: number;
  isStartingTile: boolean;
  allowsGathering: boolean;
  gatheringType?: string;
  allowsBattle: boolean;
  maxPlayerUnits: number;
  imageUrl?: string;
  iconName?: string;
  hexOffsetRow: number;
  isUnlocked: boolean;
  battlesWon: number;
  isCurrentLocation: boolean;
  hasResources: boolean;
  hasMonsters: boolean;
  highestSpawnDifficulty?: string;
}

interface Town {
  id: number;
  name: string;
  townType: string;
  worldX: number;
  worldY: number;
  isLiberated: boolean;
}

interface HexGridProps {
  tiles: HexTile[];
  currentTileId: number;
  onTileClick: (tile: HexTile) => void;
  zoom?: number;
  centerX?: number;
  centerY?: number;
  viewportWidth?: number;  // Width of visible viewport in SVG units
  viewportHeight?: number; // Height of visible viewport in SVG units
  towns?: Town[];  // Optional towns to display
}

const HexGrid: React.FC<HexGridProps> = ({
  tiles,
  currentTileId,
  onTileClick,
  zoom = 1,
  centerX = 0,
  centerY = 0,
  viewportWidth = 800,
  viewportHeight = 600,
  towns = []
}) => {
  // Hex constants (for pointy-top hexagons using axial coordinates)
  const HEX_SIZE = 40; // Radius from center to vertex
  const HEX_WIDTH = Math.sqrt(3) * HEX_SIZE; // Width for pointy-top hex
  const HEX_HEIGHT = HEX_SIZE * 2; // Height for pointy-top hex

  // Calculate hex position using offset (odd-r) coordinates for pointy-top hexagons.
  // Only odd rows shift right by half a hex, and the shift resets each row instead
  // of accumulating like pure-axial layout does - this keeps the map's left/right
  // edges vertical (rectangular outline) rather than shearing it into a rhombus.
  const getHexPosition = (q: number, r: number): { cx: number; cy: number } => {
    const cx = HEX_SIZE * Math.sqrt(3) * (q + 0.5 * (r & 1));
    const cy = HEX_SIZE * (3 / 2) * r;
    return { cx, cy };
  };

  // Generate hexagon path (pointy-top orientation)
  const getHexPath = (cx: number, cy: number, size: number): string => {
    const points: [number, number][] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2; // Start from top point for pointy-top
      const x = cx + size * Math.cos(angle);
      const y = cy + size * Math.sin(angle);
      points.push([x, y]);
    }
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ') + ' Z';
  };

  // Get biome color
  const getBiomeColor = (biome: string, isLocked: boolean): string => {
    if (isLocked) return '#2a2a3e';

    const colors: { [key: string]: string } = {
      'Plains': '#7cb342',
      'Grassland': '#9ccc65',
      'Forest': '#558b2f',
      'Mountain': '#78909c',
      'Hills': '#90a4ae',
      'Swamp': '#4e7a4e',
      'Desert': '#d4a574',
      'DarkForest': '#1b5e20',
      'Volcanic': '#bf360c',
      'Wasteland': '#5d4037',
      'Crystal': '#4fc3f7',
      'Frozen': '#81d4fa',
      'Celestial': '#9575cd',
      'Ethereal': '#ba68c8',
      'Void': '#311b92',
      'Chaos': '#4a148c'
    };

    return colors[biome] || '#546e7a';
  };

  // Get zone border color
  const getZoneBorderColor = (zoneLevel: number): string => {
    const colors = [
      '#66bb6a', // Zone 1 - Green
      '#42a5f5', // Zone 2 - Blue
      '#ab47bc', // Zone 3 - Purple
      '#ff7043', // Zone 4 - Orange
      '#ffa726', // Zone 5 - Amber
      '#ec407a'  // Zone 6 - Pink
    ];
    return colors[zoneLevel - 1] || '#666';
  };

  // Find the current (player) tile
  const currentTile = tiles.find(t => t.id === currentTileId);

  // Debug: Log unlocked tiles and visualize hex pattern
  // Debug effect removed - was logging tile map visualization
  React.useEffect(() => {
    // Tiles updated, component will re-render
  }, [tiles]);

  // Calculate viewBox centered on player tile
  let viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight;

  if (currentTile) {
    // Center viewBox on player tile
    const playerPos = getHexPosition(currentTile.x, currentTile.y);

    // ViewBox dimensions based on zoom (smaller viewBox = more zoomed in)
    viewBoxWidth = viewportWidth / zoom;
    viewBoxHeight = viewportHeight / zoom;

    // Convert pan offset from screen pixels to SVG units
    const panOffsetSVGX = -centerX / zoom;
    const panOffsetSVGY = -centerY / zoom;

    // Center viewBox on player position with pan offset
    viewBoxX = playerPos.cx - (viewBoxWidth / 2) + panOffsetSVGX;
    viewBoxY = playerPos.cy - (viewBoxHeight / 2) + panOffsetSVGY;
  } else {
    // Fallback: fit all tiles
    const minX = Math.min(...tiles.map(t => t.x));
    const maxX = Math.max(...tiles.map(t => t.x));
    const minY = Math.min(...tiles.map(t => t.y));
    const maxY = Math.max(...tiles.map(t => t.y));

    const startPos = getHexPosition(minX, minY);
    const endPos = getHexPosition(maxX, maxY);

    viewBoxWidth = endPos.cx - startPos.cx + HEX_WIDTH * 2;
    viewBoxHeight = endPos.cy - startPos.cy + HEX_HEIGHT * 2;
    viewBoxX = startPos.cx - HEX_WIDTH;
    viewBoxY = startPos.cy - HEX_HEIGHT;
  }

  // Get town at specific coordinates
  const getTownAtTile = (x: number, y: number): Town | undefined => {
    return towns.find(t => t.worldX === x && t.worldY === y);
  };

  // Get town icon
  const getTownIcon = (townType: string): string => {
    switch (townType) {
      case 'Mining': return '⛏️';
      case 'Forest': return '🌲';
      case 'Trading': return '🏪';
      case 'Military': return '⚔️';
      case 'Magical': return '🔮';
      default: return '🏰';
    }
  };

  // Get tile icon/emoji
  const getTileEmoji = (tile: HexTile): string => {
    // Check if this tile has a town
    const town = getTownAtTile(tile.x, tile.y);
    if (town) {
      return getTownIcon(town.townType);
    }

    if (tile.isStartingTile) return '🏠';
    if (!tile.isUnlocked) return '🔒';

    const emojiMap: { [key: string]: string } = {
      'Plains': '🌾',
      'Grassland': '🌿',
      'Forest': '🌲',
      'Mountain': '⛰️',
      'Hills': '⛰️',
      'Swamp': '🌿',
      'Desert': '🏜️',
      'DarkForest': '🌑',
      'Volcanic': '🌋',
      'Crystal': '💎',
      'Celestial': '✨',
      'Void': '🕳️'
    };

    return emojiMap[tile.biomeType] || '📍';
  };

  // Badge for the tile's highest available elite+ monster spawn, if any
  const getEliteSpawnBadge = (difficulty?: string): string | null => {
    switch (difficulty) {
      case 'Elite': return '⭐';
      case 'Boss': return '👑';
      case 'Legendary': return '💀';
      default: return null;
    }
  };

  return (
    <div className="hex-grid-container">
      <svg
        className="hex-grid-svg"
        viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: '100%',
          height: '100%'
        }}
      >
        {/* No transform needed - viewBox handles centering and zoom */}
        <g>
          {/* DEBUG: Draw connection lines between unlocked adjacent hexes */}
          {tiles.filter(t => t.isUnlocked).map(tile => {
            const { cx: cx1, cy: cy1 } = getHexPosition(tile.x, tile.y);

            // Parity-aware neighbors - tiles are odd-r offset, not pure axial (GH-78).
            const neighbors = getOddROffsetNeighbors(tile.x, tile.y);

            return neighbors.map((neighbor, idx) => {
              const neighborTile = tiles.find(t => t.x === neighbor.x && t.y === neighbor.y && t.isUnlocked);
              if (!neighborTile) return null;

              const { cx: cx2, cy: cy2 } = getHexPosition(neighbor.x, neighbor.y);

              return (
                <line
                  key={`${tile.id}-${neighborTile.id}-${idx}`}
                  x1={cx1}
                  y1={cy1}
                  x2={cx2}
                  y2={cy2}
                  stroke="#4caf50"
                  strokeWidth="2"
                  opacity="0.3"
                  strokeDasharray="5,5"
                />
              );
            });
          })}

          {/* Render hex tiles */}
          {tiles.map(tile => {
          const { cx, cy } = getHexPosition(tile.x, tile.y);
          const hexPath = getHexPath(cx, cy, HEX_SIZE);
          const fillColor = getBiomeColor(tile.biomeType, !tile.isUnlocked);
          const borderColor = tile.isCurrentLocation
            ? '#ff4444'
            : getZoneBorderColor(tile.zoneLevel);
          const borderWidth = tile.isCurrentLocation ? 4 : 2;

          return (
            <g
              key={tile.id}
              className={`hex-tile ${tile.isUnlocked ? 'unlocked' : 'locked'} ${tile.isCurrentLocation ? 'current' : ''}`}
              onClick={() => tile.isUnlocked && onTileClick(tile)}
              style={{ cursor: tile.isUnlocked ? 'pointer' : 'not-allowed' }}
            >
              {/* Hex background */}
              <path
                d={hexPath}
                fill={fillColor}
                stroke={borderColor}
                strokeWidth={borderWidth}
                opacity={tile.isUnlocked ? 1 : 0.4}
              />

              {/* Current location pulse effect */}
              {tile.isCurrentLocation && (
                <path
                  d={hexPath}
                  fill="none"
                  stroke="#ff4444"
                  strokeWidth="3"
                  opacity="0.6"
                  className="hex-pulse"
                />
              )}

              {/* Zone level indicator (small circle) */}
              <circle
                cx={cx - HEX_SIZE * 0.6}
                cy={cy - HEX_SIZE * 0.6}
                r="8"
                fill={getZoneBorderColor(tile.zoneLevel)}
                opacity="0.9"
              />
              <text
                x={cx - HEX_SIZE * 0.6}
                y={cy - HEX_SIZE * 0.6}
                textAnchor="middle"
                dominantBaseline="central"
                fill="white"
                fontSize="10"
                fontWeight="bold"
              >
                {tile.zoneLevel}
              </text>

              {/* Tile emoji/icon */}
              <text
                x={cx}
                y={cy - 8}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="20"
                className="hex-emoji"
              >
                {getTileEmoji(tile)}
              </text>

              {/* Resource/Monster indicators */}
              {tile.isUnlocked && (
                <g>
                  {tile.hasResources && (
                    <text
                      x={cx + HEX_SIZE * 0.5}
                      y={cy - HEX_SIZE * 0.5}
                      textAnchor="middle"
                      fontSize="14"
                      className="resource-indicator"
                    >
                      📦
                    </text>
                  )}
                  {tile.hasMonsters && (
                    <text
                      x={cx + HEX_SIZE * 0.5}
                      y={cy + HEX_SIZE * 0.5}
                      textAnchor="middle"
                      fontSize="14"
                      className="monster-indicator"
                    >
                      ⚔️
                    </text>
                  )}
                  {getEliteSpawnBadge(tile.highestSpawnDifficulty) && (
                    <text
                      x={cx - HEX_SIZE * 0.5}
                      y={cy + HEX_SIZE * 0.5}
                      textAnchor="middle"
                      fontSize="14"
                      className="elite-spawn-indicator"
                    >
                      {getEliteSpawnBadge(tile.highestSpawnDifficulty)}
                    </text>
                  )}
                </g>
              )}

              {/* Tile name (on hover via CSS) */}
              <text
                x={cx}
                y={cy + 15}
                textAnchor="middle"
                fill="white"
                fontSize="8"
                opacity="0.8"
                className="hex-name"
              >
                {tile.name.substring(0, 15)}
              </text>

              {/* Directional fingerpost hinting toward the nearest town */}
              {tile.isUnlocked && !tile.isCurrentLocation && !getTownAtTile(tile.x, tile.y) &&
                ((Math.abs(tile.x) * 7 + Math.abs(tile.y) * 13 + Math.abs(tile.x * tile.y)) % 3) === 0 &&
                (() => {
                  const hint = getNearestTownHint(tile.x, tile.y, towns);
                  if (!hint) return null;
                  return (
                    <g
                      className="town-hint"
                      transform={`translate(${cx - HEX_SIZE * 0.5}, ${cy + HEX_SIZE * 0.6})`}
                      opacity={hint.opacity}
                    >
                      <circle r="7" fill="rgba(0,0,0,0.55)" />
                      <g transform={`rotate(${hint.bearingDeg})`}>
                        <line x1="-3.5" y1="0" x2="3" y2="0" stroke="#f5e642" strokeWidth="1.5" strokeLinecap="round" />
                        <polyline points="0.5,-2.5 3,0 0.5,2.5" fill="none" stroke="#f5e642" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                      </g>
                    </g>
                  );
                })()
              }
            </g>
          );
        })}
        </g>
      </svg>
    </div>
  );
};

export default HexGrid;
